<script setup>
import { computed } from 'vue';

const emit = defineEmits(['dataUrl']);
const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    blockUuid: {
        type: String,
        require: true
    },
    blockContent: {
        type: Object,
        required: true
    },
    propertyName: {
        type: String,
        required: true
    },
    mediaBankEntryId: {
        type: Number,
        required: false
    },
    autoplay: {
        type: Boolean,
        required: false,
        default: false
    },
    hideControls: {
        type: Boolean,
        required: false,
        default: false
    },
    loop: {
        type: Boolean,
        required: false,
        default: false
    },
    altText: {
        type: String,
        required: false,
        default: ''
    },
    videoPosterMediaBankEntryId: {
        type: Number,
        required: false,
        default: null
    },
    lazyload: {
        type: Boolean,
        required: false,
        default: false
    }
});

const embedded = computed(() => props.blockContent?._entries?.[props.propertyName] ?? null);

const embeddedDataProp = computed(() => {
    if (!embedded.value?.url) return null;
    return {
        id: embedded.value.id,
        type: embedded.value.type,
        url: embedded.value.url,
        thumbnail_url: embedded.value.thumbnail_url,
    };
});

const dataUrl = computed(() => {
    // Skip the indirection when we have a usable embedded URL.
    if (embedded.value?.url) return null;
    if (!props.blockContent[props.propertyName]) return null;

    const u = route('block-editor-container.blocks.media-bank-entry', {
        containerRef: props.containerRef,
        blockUuid: props.blockUuid,
        propertyName: props.propertyName,
        mediaBankEntryId: props.mediaBankEntryId,
    });
    emit('dataUrl', u);
    return u;
});

const posterEmbedded = computed(() => {
    if (!props.videoPosterMediaBankEntryId) return null;
    return props.blockContent?._entries?.thumbnail_media_bank_entry_id ?? null;
});

const posterEmbeddedDataProp = computed(() => {
    if (!posterEmbedded.value?.url) return null;
    return {
        id: posterEmbedded.value.id,
        type: posterEmbedded.value.type,
        url: posterEmbedded.value.url,
        thumbnail_url: posterEmbedded.value.thumbnail_url,
    };
});

const posterDataUrl = computed(() => {
    if (posterEmbeddedDataProp.value) return null;
    if (!props.videoPosterMediaBankEntryId) return null;
    if (!props.blockContent[props.propertyName]) return null;

    return route('block-editor-container.blocks.media-bank-entry', {
        containerRef: props.containerRef,
        blockUuid: props.blockUuid,
        propertyName: 'thumbnail_media_bank_entry_id',
        mediaBankEntryId: props.videoPosterMediaBankEntryId,
    });
});
</script>
<template>
    <media-bank-entries-display
        v-if="embeddedDataProp"
        :data="embeddedDataProp"
        :autoplay="autoplay"
        :alt-text="altText"
        :video-poster-url="posterDataUrl"
        :video-poster-data="posterEmbeddedDataProp"
        :hide-controls="hideControls"
        :loop="loop"
        :lazyload="lazyload"
    >
        <template v-if="$slots['entry']" #entry="{ entry }">
            <slot name="entry" :entry="entry"></slot>
        </template>
        <template v-if="$slots['spinner']" #spinner>
            <slot name="spinner"></slot>
        </template>
        <template v-if="$slots['cloudflareVideoEntry']" #cloudflare-video-entry="{ entry, attrs }">
            <slot name="cloudflare-video-entry" :entry="entry" :attrs="attrs"></slot>
        </template>
    </media-bank-entries-display>

    <media-bank-entries-display
        v-else-if="dataUrl"
        :data-url="dataUrl"
        :autoplay="autoplay"
        :alt-text="altText"
        :video-poster-url="posterDataUrl"
        :video-poster-data="posterEmbeddedDataProp"
        :hide-controls="hideControls"
        :loop="loop"
        :lazyload="lazyload"
    >
        <template v-if="$slots['entry']" #entry="{ entry }">
            <slot name="entry" :entry="entry"></slot>
        </template>
        <template v-if="$slots['spinner']" #spinner>
            <slot name="spinner"></slot>
        </template>
        <template v-if="$slots['cloudflareVideoEntry']" #cloudflare-video-entry="{ entry, attrs }">
            <slot name="cloudflare-video-entry" :entry="entry" :attrs="attrs"></slot>
        </template>
    </media-bank-entries-display>
</template>
